import * as React from "react";
import { useEffect, useState } from "react";
import { Button, Dropdown, Header, Icon, Label, Message, Modal, Segment } from "semantic-ui-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import DiffViewer from "react-diff-viewer";
import { AiParseResult, runSingleAiRequest } from "./aiRequestsApi";
import { usePrettifier, PrettifiedEntry } from "./aiPrettify";

const KEY_ORIGINAL = 'original';
const KEY_NEW = 'new';

export interface AiRequestEditPromptModalProps {
    open: boolean;
    onClose: () => void;
    source: AiParseResult | null;
    model: string;
    modelRecognized: boolean;      // kept for compatibility
    modelOptions: string[];
}

interface NewRun {
    status: 'idle' | 'running' | 'done' | 'error';
    output?: string;
    thinkingMessage?: string;
    promptTokens?: number;
    completionTokens?: number;
    price?: number;
    durationMs?: number;
    error?: string;
}

function copy(text: string) {
    navigator.clipboard?.writeText(text);
}

export function AiRequestEditPromptModal(props: AiRequestEditPromptModalProps) {
    const { source, model, modelOptions } = props;

    const [editedPrompt, setEditedPrompt] = useState("");
    const [selectedModel, setSelectedModel] = useState(model);
    const [run, setRun] = useState<NewRun>({ status: 'idle' });

    // per-column prettify state
    const [origPretty, setOrigPretty] = useState<boolean>(true);
    const [newPretty, setNewPretty] = useState<boolean>(true);
    const [diffPretty, setDiffPretty] = useState<boolean>(true);

    const [diffMode, setDiffMode] = useState<boolean>(false);
    const [promptCollapsed, setPromptCollapsed] = useState<boolean>(false);
    const [showOriginalThinking, setShowOriginalThinking] = useState<boolean>(false);
    const [showNewThinking, setShowNewThinking] = useState<boolean>(false);
    const [promptDirty, setPromptDirty] = useState<boolean>(false);

    const { entries, ensure, invalidate, reset } = usePrettifier();

    const originalAnswer = source?.rawOutput || source?.output || "";
    const newAnswerRaw = run.output ?? (run.error ? `❌ ${run.error}` : "");

    const origEntry: PrettifiedEntry | undefined = entries[KEY_ORIGINAL];
    const newEntry: PrettifiedEntry | undefined = entries[KEY_NEW];

    const origIsJson = origEntry?.status === 'done' && !!origEntry.isJson;
    const newIsJson = newEntry?.status === 'done' && !!newEntry.isJson;

    // fresh state whenever another request is opened
    useEffect(() => {
        setEditedPrompt(source?.input || "");
        setSelectedModel(model);
        setRun({ status: 'idle' });
        setDiffMode(false);
        setPromptCollapsed(false);
        setShowOriginalThinking(false);
        setShowNewThinking(false);
        setPromptDirty(false);
        setOrigPretty(true);
        setNewPretty(true);
        setDiffPretty(true);
        reset();
    }, [source?._id, props.open, model, reset]);

    // let the backend format the original answer
    useEffect(() => {
        if (props.open && originalAnswer) ensure(KEY_ORIGINAL, originalAnswer);
    }, [props.open, originalAnswer, ensure]);

    const hasResult = run.status === 'done' || run.status === 'error';
    const modelKnown = modelOptions.includes(selectedModel);

    async function start() {
        if (!source || run.status === 'running' || !modelKnown || !editedPrompt.trim()) return;
        invalidate(KEY_NEW);
        setNewPretty(true);
        setRun({ status: 'running' });
        const started = Date.now();
        try {
            const res = await runSingleAiRequest(editedPrompt, selectedModel, { disableCache: true });
            setRun({
                status: 'done',
                output: res.output,
                thinkingMessage: res.thinkingMessage,
                promptTokens: res.promptTokens,
                completionTokens: res.completionTokens,
                price: res.price,
                durationMs: Date.now() - started
            });
            ensure(KEY_NEW, res.output);
        } catch (e: any) {
            setRun({ status: 'error', error: e?.message || String(e), durationMs: Date.now() - started });
        }
    }

    function resetPrompt() {
        setEditedPrompt(source?.input || "");
        setPromptDirty(false);
    }

    // ---- displayed text per column (each column formatted independently) ----
    const origText = (origPretty && origIsJson && origEntry?.text != null) ? origEntry.text : originalAnswer;
    const newText = (newPretty && newIsJson && newEntry?.text != null) ? newEntry.text : newAnswerRaw;

    // ---- diff: both sides must share the same formatting ----
    const diffBothJson = origIsJson && newIsJson;
    const diffUsePretty = diffPretty && diffBothJson;
    const diffOld = diffUsePretty ? (origEntry!.text as string) : originalAnswer;
    const diffNew = diffUsePretty ? (newEntry!.text as string) : newAnswerRaw;

    // ---- verdict, based on the canonical form delivered by the backend ----
    function compactOf(entry: PrettifiedEntry | undefined, raw: string): string {
        if (entry?.status === 'done' && entry.isJson && entry.compact) return entry.compact;
        return (raw || "").trim();
    }

    const answersIdentical =
        run.status === 'done' &&
        compactOf(origEntry, originalAnswer) === compactOf(newEntry, newAnswerRaw);

    function verdictLabel(size: 'mini' | 'small') {
        if (run.status !== 'done') return null;
        return answersIdentical
            ? <Label size={size} color="green"><Icon name="check" /> identical</Label>
            : <Label size={size} color="red"><Icon name="exchange" /> different</Label>;
    }

    /** Prettify / Unprettify button, or a red hint explaining why it is not possible */
    function prettifyControl(
        entry: PrettifiedEntry | undefined,
        active: boolean,
        toggle: () => void
    ) {
        if (!entry || entry.status === 'loading') {
            return <Button size="mini" basic loading disabled><Icon name="code" /> Prettify</Button>;
        }
        if (entry.status === 'error') {
            return (
                <Label size="mini" color="red" title={entry.error}>
                    <Icon name="warning sign" /> format failed
                </Label>
            );
        }
        if (!entry.isJson) {
            return (
                <Label size="mini" color="red" title="The backend could not repair this text into JSON.">
                    <Icon name="warning sign" /> not JSON – can&apos;t prettify
                </Label>
            );
        }
        return (
            <Button size="mini" basic={!active} color={active ? 'blue' : undefined} onClick={toggle}>
                <Icon name="code" /> {active ? 'Unprettify' : 'Prettify'}
            </Button>
        );
    }

    function drawStats(
        stats: { promptTokens?: number; completionTokens?: number; price?: number },
        durationMs?: number
    ) {
        const { promptTokens, completionTokens, price } = stats;
        if (promptTokens == null && completionTokens == null && price == null && durationMs == null) return null;
        return (
            <span style={{ fontSize: '0.75em', color: '#666', display: 'inline-flex', gap: 8 }}>
                {promptTokens != null && <span>in {promptTokens.toLocaleString()}</span>}
                {completionTokens != null && <span>out {completionTokens.toLocaleString()}</span>}
                {price != null && <span>${price.toFixed(4)}</span>}
                {durationMs != null && <span>{(durationMs / 1000).toFixed(1)}s</span>}
            </span>
        );
    }

    const colHeaderStyle: React.CSSProperties = {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 6, marginBottom: 6, flexWrap: 'wrap', flexShrink: 0
    };

    const thinkingBoxStyle: React.CSSProperties = {
        marginTop: 6, maxHeight: 180, overflow: 'auto', flexShrink: 0,
        backgroundColor: '#f3e5f5', border: '1px dashed #9c27b0',
        padding: '8px 10px', fontSize: '0.82em', whiteSpace: 'pre-wrap',
        fontFamily: 'monospace', color: '#222'
    };

    function renderOriginalColumn() {
        if (!source) return null;
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden',
                border: '2px solid #2185d0', borderRadius: 6, padding: 8, backgroundColor: '#f7fbff'
            }}>
                <div style={colHeaderStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: '0.9em' }}>Original response</strong>
                        <Label size="mini" color="blue">cached · {source.model}</Label>
                        {drawStats({
                            promptTokens: source.promptTokens,
                            completionTokens: source.completionTokens,
                            price: source.totalPrice
                        })}
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {prettifyControl(origEntry, origPretty, () => setOrigPretty(p => !p))}
                        {source.thinkingMessage && (
                            <Button size="mini" basic icon="lightbulb outline" title="thinking"
                                    onClick={() => setShowOriginalThinking(v => !v)} />
                        )}
                        <Button size="mini" basic icon="copy" onClick={() => copy(originalAnswer)} />
                    </div>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                    <AceEditor
                        key={`edit-orig-${source._id}-${origPretty && origIsJson ? 'p' : 'r'}`}
                        mode="json"
                        theme="monokai"
                        value={origText}
                        readOnly
                        width="100%"
                        height="100%"
                        fontSize={13}
                        showPrintMargin={false}
                        wrapEnabled={false}
                        editorProps={{ $blockScrolling: true }}
                        setOptions={{ useWorker: false, showLineNumbers: true, tabSize: 2 }}
                    />
                </div>
                {source.thinkingMessage && showOriginalThinking && (
                    <div style={thinkingBoxStyle}>{source.thinkingMessage}</div>
                )}
            </div>
        );
    }

    function renderNewColumn() {
        if (!source) return null;

        const border = run.status === 'error' ? '2px solid #db2828'
            : run.status === 'done' ? '2px solid #21ba45'
                : '1px solid #ccc';

        let body: React.ReactNode;
        if (run.status === 'idle') {
            body = <Message info size="tiny"
                            content="Edit the prompt above and press Run to generate a new response." />;
        } else if (run.status === 'running') {
            body = (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666' }}>
                    <Icon name="spinner" loading /> Running…
                </div>
            );
        } else {
            body = (
                <AceEditor
                    key={`edit-new-${source._id}-${newPretty && newIsJson ? 'p' : 'r'}`}
                    mode="json"
                    theme="monokai"
                    value={newText}
                    readOnly
                    width="100%"
                    height="100%"
                    fontSize={13}
                    showPrintMargin={false}
                    wrapEnabled={false}
                    editorProps={{ $blockScrolling: true }}
                    setOptions={{ useWorker: false, showLineNumbers: true, tabSize: 2 }}
                />
            );
        }

        return (
            <div style={{
                display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden',
                border, borderRadius: 6, padding: 8, backgroundColor: '#fff'
            }}>
                <div style={colHeaderStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: '0.9em' }}>New response</strong>
                        <Label size="mini" color="teal">{selectedModel}</Label>
                        {promptDirty && <Label size="mini" color="orange">edited prompt</Label>}
                        {verdictLabel('mini')}
                        {run.status === 'running' && <Icon name="spinner" loading />}
                        {run.status === 'error' && <Icon name="times circle" color="red" />}
                        {run.status === 'done' && drawStats({
                            promptTokens: run.promptTokens,
                            completionTokens: run.completionTokens,
                            price: run.price
                        }, run.durationMs)}
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {run.status === 'done' &&
                            prettifyControl(newEntry, newPretty, () => setNewPretty(p => !p))}
                        {run.thinkingMessage && (
                            <Button size="mini" basic icon="lightbulb outline" title="thinking"
                                    onClick={() => setShowNewThinking(v => !v)} />
                        )}
                        {run.status === 'done' && run.output && (
                            <Button size="mini" basic icon="copy" onClick={() => copy(run.output!)} />
                        )}
                    </div>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                    {body}
                </div>
                {run.thinkingMessage && showNewThinking && (
                    <div style={thinkingBoxStyle}>{run.thinkingMessage}</div>
                )}
            </div>
        );
    }

    function renderDiff() {
        return (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '0.9em' }}>Original</strong>
                    <Icon name="arrow right" />
                    <strong style={{ fontSize: '0.9em' }}>New ({selectedModel})</strong>
                    {verdictLabel('small')}
                    {diffBothJson ? (
                        <Button size="mini" basic={!diffPretty} color={diffPretty ? 'blue' : undefined}
                                onClick={() => setDiffPretty(p => !p)}>
                            <Icon name="code" /> {diffPretty ? 'Unprettify both' : 'Prettify both'}
                        </Button>
                    ) : (
                        <Label size="mini" color="red">
                            <Icon name="warning sign" />
                            {!origIsJson && !newIsJson
                                ? 'both responses are not JSON – diffing raw text'
                                : !origIsJson
                                    ? 'original is not JSON – diffing raw text'
                                    : 'new response is not JSON – diffing raw text'}
                        </Label>
                    )}
                </div>
                <div style={{ fontSize: 12 }}>
                    <DiffViewer oldValue={diffOld} newValue={diffNew} splitView={true} />
                </div>
            </div>
        );
    }

    if (!source) return null;

    return (
        <Modal open={props.open} onClose={props.onClose} size="fullscreen">
            <Header style={{ padding: '12px 20px', backgroundColor: '#2c3e50', color: '#fff', margin: 0 }}>
                <Icon name="edit outline" /> Edit prompt &amp; rerun
                <Label color="blue" size="small" style={{ marginLeft: 10 }}>{source.model}</Label>
                <Label size="small" style={{ marginLeft: 6 }}>cache disabled</Label>
                {promptDirty && (
                    <Label color="orange" size="small" style={{ marginLeft: 6 }}>prompt modified</Label>
                )}
            </Header>

            <Modal.Content style={{
                padding: '12px 14px',
                height: 'calc(100vh - 140px)',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Toolbar */}
                <Segment style={{ flexShrink: 0, padding: '10px 12px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.85em', color: '#666' }}>Model:</span>
                        <Dropdown
                            selection
                            search
                            disabled={run.status === 'running'}
                            options={modelOptions.map(m => ({ key: m, text: m, value: m }))}
                            value={selectedModel}
                            onChange={(_, d) => setSelectedModel(d.value as string)}
                            style={{ minWidth: 240 }}
                        />

                        <Button
                            primary
                            loading={run.status === 'running'}
                            disabled={run.status === 'running' || !modelKnown || !editedPrompt.trim()}
                            onClick={start}
                        >
                            <Icon name="play" /> Run
                        </Button>
                        <Button basic disabled={run.status === 'running' || !promptDirty} onClick={resetPrompt}>
                            <Icon name="undo" /> Reset prompt
                        </Button>

                        {hasResult && (
                            <Button.Group size="small">
                                <Button active={!diffMode} onClick={() => setDiffMode(false)}>Side by side</Button>
                                <Button active={diffMode} onClick={() => setDiffMode(true)}
                                        disabled={run.status !== 'done'}>Diff</Button>
                            </Button.Group>
                        )}

                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                            {verdictLabel('small')}
                            <span style={{ fontSize: '0.85em', color: '#666' }}>
                                prompt: {editedPrompt.length.toLocaleString()} chars
                            </span>
                        </div>
                    </div>

                    {!modelKnown && (
                        <Message
                            warning size="tiny" style={{ marginTop: 8 }}
                            content={`Model "${selectedModel}" is not a known ParseTextModel - pick a model above to run.`}
                        />
                    )}
                </Segment>

                {/* Editable prompt */}
                <div style={{
                    flexShrink: 0,
                    marginBottom: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    height: promptCollapsed ? 'auto' : '34%',
                    minHeight: 0
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <strong style={{ fontSize: '0.9em' }}>
                            <Icon name="pencil" /> Prompt (editable)
                        </strong>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <Button size="mini" basic icon="copy" onClick={() => copy(editedPrompt)} />
                            <Button size="mini" basic onClick={() => setPromptCollapsed(c => !c)}>
                                <Icon name={promptCollapsed ? 'chevron down' : 'chevron up'} />
                                {promptCollapsed ? 'Expand' : 'Collapse'}
                            </Button>
                        </div>
                    </div>
                    {!promptCollapsed && (
                        <div style={{ flex: 1, minHeight: 0 }}>
                            <AceEditor
                                mode="json"
                                theme="monokai"
                                value={editedPrompt}
                                onChange={(v) => {
                                    setEditedPrompt(v);
                                    setPromptDirty(v !== (source.input || ""));
                                }}
                                width="100%"
                                height="100%"
                                fontSize={13}
                                showPrintMargin={false}
                                wrapEnabled
                                editorProps={{ $blockScrolling: true }}
                                setOptions={{ useWorker: false, showLineNumbers: true, tabSize: 2 }}
                            />
                        </div>
                    )}
                </div>

                {/* Results */}
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    {diffMode && run.status === 'done' ? renderDiff() : (
                        <div style={{
                            flex: 1, minHeight: 0, display: 'grid',
                            gridTemplateColumns: '1fr 1fr', gap: 10
                        }}>
                            {renderOriginalColumn()}
                            {renderNewColumn()}
                        </div>
                    )}
                </div>
            </Modal.Content>

            <Modal.Actions>
                <Button onClick={props.onClose}>Close</Button>
            </Modal.Actions>
        </Modal>
    );
}